/**
 * The little markup that server and translation texts carry, parsed into plain text parts so it can be shown
 * without v-html. The game messages from the backend mark card names with <span class='bold'>…</span> and join
 * lines with <br/> (CardHandler.cs, BaseMultiGame.AddMessage, BasePlayer.AddMessage), and one translation
 * (Confirm_Give_Card_Text) uses the same span. Those are the only tags understood; any other tag, and any
 * unmatched closing tag, stays as visible text. Player names end up inside these texts, so nothing here may
 * ever be turned into HTML: render the parts as text nodes (src/dod/components/DMarkup.vue).
 *
 * The result is a list of lines, each a list of { text, bold } parts.
 */

const TOKEN = /<br\s*\/?>|<span\b[^<>]*>|<\/span\s*>/gi;
const BOLD_SPAN = /^<span\s+class\s*=\s*(['"])bold\1\s*>$/i;

const ENTITIES = { amp: "&", lt: "<", gt: ">", quot: "\"", apos: "'", nbsp: "\u00a0" };
const ENTITY = /&(?:#(\d{1,7})|#x([0-9a-f]{1,6})|([a-z]+));/gi;
const PLACEHOLDER = /\{\{([^{}]+)\}\}/g;

export function parseMarkup(input) {
    if (input === null || input === undefined)
        return [];
    const source = String(input);
    if (source === "")
        return [];

    const lines = [[]];
    const open = []; // one entry per open <span>: true for a bold span, false for one shown as text
    let last = 0;
    let match;

    const add = (text, bold) => {
        if (!text)
            return;
        const line = lines[lines.length - 1];
        const previous = line[line.length - 1];
        if (previous && previous.bold === bold)
            previous.text += text;
        else
            line.push({ text, bold });
    };
    const bold = () => open.includes(true);

    TOKEN.lastIndex = 0;
    while ((match = TOKEN.exec(source)) !== null) {
        add(decodeEntities(source.slice(last, match.index)), bold());
        last = TOKEN.lastIndex;
        const tag = match[0];

        if (tag[1].toLowerCase() === "b") {
            lines.push([]);
            open.length = 0; // the backend never runs a span over a line break: a stray one in a name ends here
        } else if (tag[1] === "/") {
            if (open.length === 0 || open.pop() === false)
                add(tag, bold());
        } else if (BOLD_SPAN.test(tag)) {
            open.push(true);
        } else {
            open.push(false);
            add(tag, bold());
        }
    }
    add(decodeEntities(source.slice(last)), bold());
    return lines;
}

/**
 * Replaces {{KEY}} in the text parts with the values, after parsing: a value (a player's name) is always
 * text, even if it looks like markup.
 */
export function fillPlaceholders(lines, values) {
    if (!values || Object.keys(values).length === 0)
        return lines;
    const fill = (text) => text.replace(PLACEHOLDER, (placeholder, key) => {
        if (!Object.prototype.hasOwnProperty.call(values, key))
            return placeholder;
        const value = values[key];
        return value === null || value === undefined ? "" : String(value);
    });
    return lines.map((line) => line.map((part) => ({ text: fill(part.text), bold: part.bold })));
}

function decodeEntities(text) {
    if (!text.includes("&"))
        return text;
    return text.replace(ENTITY, (entity, decimal, hex, name) => {
        if (name)
            return Object.prototype.hasOwnProperty.call(ENTITIES, name.toLowerCase()) ? ENTITIES[name.toLowerCase()] : entity;
        const code = decimal ? parseInt(decimal, 10) : parseInt(hex, 16);
        if (code <= 0 || code > 0x10ffff || (code >= 0xd800 && code <= 0xdfff))
            return entity;
        return String.fromCodePoint(code);
    });
}
